import { Dialog } from "@headlessui/react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import classNames from "classnames";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

import { CloseButton } from "@/components/closeButton";
import FilePreview from "@/components/preview";

export type PreviewNavigationDirection = "previous" | "next" | null;

type PreviewMeta = {
  assetType: string;
  canPreview: boolean;
};

type FilePreviewDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | null;
  previewMeta: PreviewMeta;
  previewUrl: string | null;
  actions?: ReactNode;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  navigatingDirection?: PreviewNavigationDirection;
  previousUnavailableMessage?: string;
  nextUnavailableMessage?: string;
};

export default function FilePreviewDialog({
  isOpen,
  onClose,
  title,
  subtitle = null,
  previewMeta,
  previewUrl,
  actions = null,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  navigatingDirection = null,
  previousUnavailableMessage,
  nextUnavailableMessage,
}: FilePreviewDialogProps) {
  const previewPanelRef = useRef<HTMLDivElement | null>(null);
  const previousUnavailableTooltipId = useId();
  const nextUnavailableTooltipId = useId();
  const hasNavigation = !!onPrevious && !!onNext;
  const previousIsDisabled = !hasPrevious || !!navigatingDirection;
  const nextIsDisabled = !hasNext || !!navigatingDirection;
  const showPreviousUnavailableTooltip =
    previousIsDisabled && !!previousUnavailableMessage;
  const showNextUnavailableTooltip = nextIsDisabled && !!nextUnavailableMessage;
  const previousTitle = showPreviousUnavailableTooltip
    ? previousUnavailableMessage
    : "Previous preview";
  const nextTitle = showNextUnavailableTooltip
    ? nextUnavailableMessage
    : "Next preview";

  useEffect(() => {
    if (!isOpen || !hasNavigation) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      const isTyping =
        target instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);

      if (isTyping) {
        return;
      }

      if (event.key === "ArrowLeft" && hasPrevious) {
        event.preventDefault();
        onPrevious();
      }

      if (event.key === "ArrowRight" && hasNext) {
        event.preventDefault();
        onNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasNavigation, hasNext, hasPrevious, isOpen, onNext, onPrevious]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      initialFocus={previewPanelRef}
      className="fixed inset-0 z-[200] overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center px-4 py-6">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
        <div className="relative z-[205] flex w-full max-w-7xl items-center justify-center md:px-16">
          {hasNavigation && (
            <span
              className="group absolute left-2 top-1/2 z-[210] hidden -translate-y-1/2 md:flex"
              title={showPreviousUnavailableTooltip ? undefined : previousTitle}
            >
              <button
                type="button"
                aria-label="Previous preview"
                aria-describedby={
                  showPreviousUnavailableTooltip
                    ? previousUnavailableTooltipId
                    : undefined
                }
                disabled={previousIsDisabled}
                onClick={onPrevious}
                className={classNames(
                  "flex h-11 w-11 items-center justify-center rounded-full bg-white text-blue-300 shadow-lg transition-[opacity,transform,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100 active:scale-[0.96]",
                  !previousIsDisabled
                    ? "opacity-95 hover:text-blue-100"
                    : "cursor-not-allowed opacity-30"
                )}
              >
                <FaChevronLeft />
              </button>
              {showPreviousUnavailableTooltip && (
                <span
                  id={previousUnavailableTooltipId}
                  className="pointer-events-none absolute left-0 top-full mt-2 w-56 rounded bg-gray-800 px-3 py-2 text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
                >
                  {previousUnavailableMessage}
                </span>
              )}
            </span>
          )}
          <div
            ref={previewPanelRef}
            tabIndex={-1}
            className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-md bg-white text-left shadow-xl outline-none"
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
              <div className="min-w-0">
                <Dialog.Title className="break-words text-lg font-semibold leading-tight text-blue-300">
                  {title}
                </Dialog.Title>
                {subtitle && (
                  <p className="mt-1 break-words text-xs text-grey-200">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                type="button"
                aria-label="Close preview"
                className="shrink-0"
                onClick={onClose}
              >
                <CloseButton handleClick={onClose} className="" />
              </button>
            </div>

            <div className="min-h-[18rem] flex-1 overflow-auto bg-zinc-50 p-4">
              {previewMeta.canPreview && previewUrl ? (
                <FilePreview
                  key={`${previewMeta.assetType}:${previewUrl}`}
                  type={previewMeta.assetType}
                  src={previewUrl}
                />
              ) : (
                <div className="py-16 text-center text-grey-200">
                  Preview unavailable for this file type.
                </div>
              )}
            </div>

            <div className="flex flex-col items-center gap-3 border-t border-gray-200 px-5 py-4">
              {hasNavigation && (
                <div className="flex items-center justify-center gap-2 md:hidden">
                  <button
                    type="button"
                    title={previousTitle}
                    className="ox-button flex min-h-10 items-center rounded-md px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={previousIsDisabled}
                    onClick={onPrevious}
                  >
                    <FaChevronLeft className="mr-2" />
                    Previous
                  </button>
                  <button
                    type="button"
                    title={nextTitle}
                    className="ox-button flex min-h-10 items-center rounded-md px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={nextIsDisabled}
                    onClick={onNext}
                  >
                    Next
                    <FaChevronRight className="ml-2" />
                  </button>
                </div>
              )}
              {actions && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {actions}
                </div>
              )}
            </div>
          </div>
          {hasNavigation && (
            <span
              className="group absolute right-2 top-1/2 z-[210] hidden -translate-y-1/2 md:flex"
              title={showNextUnavailableTooltip ? undefined : nextTitle}
            >
              <button
                type="button"
                aria-label="Next preview"
                aria-describedby={
                  showNextUnavailableTooltip
                    ? nextUnavailableTooltipId
                    : undefined
                }
                disabled={nextIsDisabled}
                onClick={onNext}
                className={classNames(
                  "flex h-11 w-11 items-center justify-center rounded-full bg-white text-blue-300 shadow-lg transition-[opacity,transform,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100 active:scale-[0.96]",
                  !nextIsDisabled
                    ? "opacity-95 hover:text-blue-100"
                    : "cursor-not-allowed opacity-30"
                )}
              >
                <FaChevronRight />
              </button>
              {showNextUnavailableTooltip && (
                <span
                  id={nextUnavailableTooltipId}
                  className="pointer-events-none absolute right-0 top-full mt-2 w-56 rounded bg-gray-800 px-3 py-2 text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
                >
                  {nextUnavailableMessage}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </Dialog>
  );
}
